import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { CheckCircle } from 'lucide-react';
import { Footer } from '../components/LegalDisclaimers';
import { readPendingQuestion } from '../lib/pendingAdvisorQuestion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SubscriptionSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [status, setStatus] = useState('checking');
  const [plan, setPlan] = useState('pro');
  const [receipt, setReceipt] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      navigate('/pricing');
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/subscription/status/${sessionId}`,
          { withCredentials: true }
        );

        if (response.data.plan) {
          setPlan(response.data.plan);
        }
        if (response.data.subtotal != null) {
          setReceipt({
            subtotal: response.data.subtotal,
            tps: response.data.tps,
            tvq: response.data.tvq,
            total: response.data.total,
            billing_period: response.data.billing_period,
          });
        }

        if (response.data.payment_status === 'paid') {
          setStatus('success');
        } else if (response.data.status === 'expired') {
          setStatus('failed');
        } else if (attempts < maxAttempts) {
          setTimeout(() => {
            setAttempts(attempts + 1);
          }, 2000);
        } else {
          setStatus('failed');
        }
      } catch (error) {
        console.error('Payment status check error:', error);
        if (attempts < maxAttempts) {
          setTimeout(() => {
            setAttempts(attempts + 1);
          }, 2000);
        } else {
          setStatus('failed');
        }
      }
    };

    checkPaymentStatus();
  }, [searchParams, navigate, attempts]);

  const planLabel = plan === 'premium' ? t('common.premium') : t('common.pro');
  const fmt = (n) =>
    typeof n === 'number' ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0E14] flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {status === 'checking' && (
          <>
            <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-2 border-primary mb-6"></div>
            <h1 className="font-primary font-bold text-2xl text-slate-900 dark:text-white mb-2">{t('subscription.processing')}</h1>
            <p className="text-slate-600 dark:text-slate-400">{t('subscription.processingDesc')}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${plan === 'premium' ? 'bg-gradient-to-r from-[#FFD700] to-[#FFA500]' : 'bg-[#45C04A]'}`}>
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            <h1 data-testid="success-title" className="font-primary font-bold text-3xl text-slate-900 dark:text-white mb-4">
              {t('subscription.welcomeTo', { plan: planLabel })}
            </h1>
            <p className="text-lg text-slate-600 dark:text-slate-400 mb-8">
              {t('subscription.successMessage')}
            </p>
            {receipt && (
              <div
                data-testid="subscription-receipt"
                className="mx-auto max-w-sm text-left rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#131824] p-5 mb-8 space-y-2 text-sm"
              >
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 dark:text-slate-400">{t('subscription.receipt.plan')}</span>
                  <span className="font-semibold text-slate-900 dark:text-white">{planLabel}</span>
                </div>
                {receipt.billing_period && (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500 dark:text-slate-400">{t('subscription.receipt.billingPeriod')}</span>
                    <span className="font-semibold text-slate-900 dark:text-white">
                      {receipt.billing_period === 'yearly'
                        ? t('subscription.receipt.billingYearly')
                        : t('subscription.receipt.billingMonthly')}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 dark:text-slate-400">{t('subscription.receipt.subtotal')}</span>
                  <span data-testid="receipt-subtotal" className="text-slate-900 dark:text-white">${fmt(receipt.subtotal)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 dark:text-slate-400">{t('subscription.receipt.tps')}</span>
                  <span data-testid="receipt-tps" className="text-slate-900 dark:text-white">${fmt(receipt.tps)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 dark:text-slate-400">{t('subscription.receipt.tvq')}</span>
                  <span data-testid="receipt-tvq" className="text-slate-900 dark:text-white">${fmt(receipt.tvq)}</span>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-slate-200 dark:border-slate-700 font-semibold">
                  <span className="text-slate-900 dark:text-white">{t('subscription.receipt.total')}</span>
                  <span data-testid="receipt-total" className="text-[#12355B] dark:text-[#45C04A]">${fmt(receipt.total)} CAD</span>
                </div>
              </div>
            )}
            <Button
              data-testid="go-to-dashboard-button"
              onClick={() => {
                // If the user upgraded from a portfolio nudge, route them
                // back to that deal so the Advisor can auto-replay their
                // question as their first Premium query.
                const pending = plan === 'premium' ? readPendingQuestion() : null;
                if (pending && pending.dealId) {
                  navigate(`/deal/${pending.dealId}?advisorReturn=1`);
                } else {
                  navigate('/dashboard');
                }
              }}
              className="bg-[#12355B] hover:bg-[#12355B]/90 dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white rounded-2xl px-8 py-3"
            >
              {plan === 'premium' && readPendingQuestion()?.dealId
                ? t('subscription.returnToAdvisor')
                : t('subscription.goToDashboard')}
            </Button>
          </>
        )}

        {status === 'failed' && (
          <>
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full mb-6">
              <span className="text-4xl">!</span>
            </div>
            <h1 className="font-primary font-bold text-3xl text-slate-900 dark:text-white mb-4">{t('subscription.failedTitle')}</h1>
            <p className="text-lg text-slate-600 dark:text-slate-400 mb-8">
              {t('subscription.failedMessage')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                data-testid="retry-button"
                onClick={() => navigate('/pricing')}
                className="bg-[#12355B] hover:bg-[#12355B]/90 dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white rounded-2xl px-6 py-2"
              >
                {t('subscription.tryAgain')}
              </Button>
              <Button
                data-testid="contact-support-button"
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white hover:bg-slate-50 dark:hover:bg-slate-800 rounded-2xl px-6 py-2"
              >
                {t('subscription.goToDashboard')}
              </Button>
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}
